/**
 * PlatformHeightController.js
 *
 * Anchor-based pose UI. Lets the user nudge a character's face trim,
 * lock its eye height to whatever is currently on screen, shift the
 * global face anchor, and export the merged pose map.
 *
 * Expects a host element <div id="platform-controls"> somewhere in
 * the page. Refreshes itself on the `modelLoadingComplete` event.
 */

import { SCENE_FRAMING } from "./SceneFraming.js";
import {
    clearGlobalFaceAnchorOffset,
    clearOverride,
    computePose,
    exportPoses,
    getGlobalFaceAnchorOffset,
    getPoseConfig,
    saveOverride,
    setGlobalFaceAnchorOffset,
} from "./CharacterPoseConfig.js";

const FACE_TRIM_MIN = -50;
const FACE_TRIM_MAX = 50;
const ANCHOR_MIN = -50;
const ANCHOR_MAX = 50;

const SOURCE_BADGE = {
    manual: "bg-danger",
    override: "bg-info",
    runtime: "bg-success",
    cache: "bg-primary",
    config: "bg-secondary",
    default: "bg-warning",
};

export class PlatformHeightController {
    constructor(modelViewer) {
        this.modelViewer = modelViewer;
        this.host = null;
        this.els = {};
        this._modelLoadHandler = () => this.refresh();

        this._init();
    }

    _init() {
        const host = document.getElementById("platform-controls");
        if (!host) {
            console.warn(
                "[PlatformHeightController] #platform-controls container not found",
            );
            return;
        }
        this.host = host;

        const wrap = document.createElement("div");
        wrap.className = "mt-3 anchor-pose-controls";
        wrap.innerHTML = `
            <div class="border-top pt-3">
                <h6 class="text-light mb-2">
                    <i class="fas fa-arrows-alt-v"></i> Character Pose
                </h6>
                <div class="small text-light mb-2">
                    <div>Anchor Y: <span data-aps="anchor">—</span></div>
                    <div>Model: <span data-aps="name">—</span></div>
                    <div>
                        Eye height:
                        <span data-aps="eye">—</span>
                        <span data-aps="source-badge" class="badge ms-1 bg-secondary">—</span>
                    </div>
                    <div>Model Y: <span data-aps="modelY">—</span></div>
                </div>
                <div class="mb-2">
                    <label class="form-label small text-light">
                        Face Trim
                        (<span data-aps="trim-val">0</span>)
                    </label>
                    <input type="range" class="form-range"
                           data-aps="trim"
                           min="${FACE_TRIM_MIN}" max="${FACE_TRIM_MAX}"
                           step="0.5" value="0">
                </div>
                <div class="mb-2">
                    <label class="form-label small text-light">
                        Global Face Anchor
                        (<span data-aps="anchor-val">0</span>)
                    </label>
                    <input type="range" class="form-range"
                           data-aps="anchor-slider"
                           min="${ANCHOR_MIN}" max="${ANCHOR_MAX}"
                           step="0.5" value="0">
                </div>
                <div class="d-flex flex-wrap gap-2">
                    <button data-aps="reset" class="btn btn-outline-light btn-sm">
                        <i class="fas fa-undo"></i> Reset
                    </button>
                    <button data-aps="save" class="btn btn-primary btn-sm">
                        <i class="fas fa-save"></i> Save
                    </button>
                    <button data-aps="lock" class="btn btn-warning btn-sm">
                        <i class="fas fa-crosshairs"></i> Override w/ current
                    </button>
                    <button data-aps="reset-anchor" class="btn btn-outline-secondary btn-sm">
                        <i class="fas fa-globe"></i> Reset anchor
                    </button>
                    <button data-aps="export" class="btn btn-outline-info btn-sm">
                        <i class="fas fa-file-export"></i> Export JSON
                    </button>
                </div>
                <div class="mt-2">
                    <small class="text-muted">
                        Edits apply live. Save persists per-character;
                        Override locks the on-screen eye Y as the new eye height.
                    </small>
                </div>
            </div>
        `;
        host.appendChild(wrap);

        const q = (sel) => wrap.querySelector(`[data-aps="${sel}"]`);
        this.els = {
            anchor: q("anchor"),
            name: q("name"),
            eye: q("eye"),
            sourceBadge: q("source-badge"),
            modelY: q("modelY"),
            trim: q("trim"),
            trimVal: q("trim-val"),
            anchorSlider: q("anchor-slider"),
            anchorVal: q("anchor-val"),
            reset: q("reset"),
            save: q("save"),
            lock: q("lock"),
            resetAnchor: q("reset-anchor"),
            export: q("export"),
        };

        this._wireEvents();
        document.addEventListener(
            "modelLoadingComplete",
            this._modelLoadHandler,
        );
        this.refresh();
    }

    _wireEvents() {
        const { trim, anchorSlider, reset, save, lock, resetAnchor, export: exp } = this.els;

        if (trim) {
            trim.addEventListener("input", (e) => {
                const v = parseFloat(e.target.value);
                if (this.els.trimVal) this.els.trimVal.textContent = v.toFixed(1);
                this._previewFaceTrim(v);
            });
        }
        if (anchorSlider) {
            anchorSlider.addEventListener("input", (e) => {
                const v = parseFloat(e.target.value);
                if (this.els.anchorVal) this.els.anchorVal.textContent = v.toFixed(1);
                setGlobalFaceAnchorOffset(v);
                this._reapply();
                this._refreshReadout();
            });
        }
        if (reset) {
            reset.addEventListener("click", () => this._resetCurrent());
        }
        if (save) {
            save.addEventListener("click", () => this._saveCurrent());
        }
        if (lock) {
            lock.addEventListener("click", () => this._overrideWithCurrent());
        }
        if (resetAnchor) {
            resetAnchor.addEventListener("click", () => {
                clearGlobalFaceAnchorOffset();
                this._reapply();
                this.refresh();
            });
        }
        if (exp) {
            exp.addEventListener("click", () => this._exportJson());
        }
    }

    _currentModelId() {
        return this.modelViewer && this.modelViewer.currentModelKey
            ? this.modelViewer.currentModelKey
            : null;
    }

    refresh() {
        const id = this._currentModelId();
        const pose = computePose(id);

        if (this.els.anchor) {
            this.els.anchor.textContent = pose.effectiveAnchorY.toFixed(1) +
                ` (base ${pose.faceAnchorY} + ${pose.globalAnchorOffset.toFixed(1)})`;
        }
        if (this.els.name) {
            this.els.name.textContent = pose.name + (id ? "" : " (none)");
        }
        if (this.els.eye) {
            this.els.eye.textContent = pose.eyeHeight.toFixed(1);
        }
        if (this.els.sourceBadge) {
            this.els.sourceBadge.textContent = pose.source;
            this.els.sourceBadge.className =
                "badge ms-1 " + (SOURCE_BADGE[pose.source] || "bg-secondary");
        }
        if (this.els.modelY) {
            this.els.modelY.textContent = pose.modelY.toFixed(1);
        }

        const cfg = id ? getPoseConfig(id) : { faceTrim: 0 };
        if (this.els.trim) {
            this.els.trim.value = String(cfg.faceTrim || 0);
        }
        if (this.els.trimVal) {
            this.els.trimVal.textContent = (cfg.faceTrim || 0).toFixed(1);
        }

        const anchor = getGlobalFaceAnchorOffset();
        if (this.els.anchorSlider) {
            this.els.anchorSlider.value = String(anchor);
        }
        if (this.els.anchorVal) {
            this.els.anchorVal.textContent = anchor.toFixed(1);
        }
    }

    _previewFaceTrim(value) {
        // Live preview only — does not persist. Use Save to commit the
        // currently-selected trim value to localStorage.
        const id = this._currentModelId();
        const mv = this.modelViewer;
        if (!id || !mv) return;
        const pose = computePose(id, { faceTrimOverride: value });
        if (mv.model) mv.model.position.y = pose.modelY;
        if (mv.platform) mv.platform.position.y = pose.platformY;
        if (this.els.modelY) this.els.modelY.textContent = pose.modelY.toFixed(1);
    }

    _refreshReadout() {
        const id = this._currentModelId();
        const pose = computePose(id);
        if (this.els.modelY) this.els.modelY.textContent = pose.modelY.toFixed(1);
        if (this.els.eye) this.els.eye.textContent = pose.eyeHeight.toFixed(1);
        if (this.els.sourceBadge) {
            this.els.sourceBadge.textContent = pose.source;
            this.els.sourceBadge.className =
                "badge ms-1 " + (SOURCE_BADGE[pose.source] || "bg-secondary");
        }
        if (this.els.anchor) {
            this.els.anchor.textContent = pose.effectiveAnchorY.toFixed(1) +
                ` (base ${pose.faceAnchorY} + ${pose.globalAnchorOffset.toFixed(1)})`;
        }
    }

    _reapply() {
        const id = this._currentModelId();
        const mv = this.modelViewer;
        if (!mv) return;
        // Invalidate any in-flight applyPose() load so a late platform
        // FBX callback can't overwrite our manual reapply.
        if (typeof mv._poseRequestToken === "number") {
            mv._poseRequestToken += 1;
        }
        const pose = computePose(id);
        if (mv.model) {
            mv.model.position.y = pose.modelY;
        }
        if (mv.platform) {
            mv.platform.position.y = pose.platformY;
            mv.platform.position.z = pose.platformZ;
            mv.platform.scale.set(
                pose.platformScale,
                pose.platformScale,
                pose.platformScale,
            );
        }
    }

    _resetCurrent() {
        const id = this._currentModelId();
        if (!id) return;
        clearOverride(id);
        this._reapply();
        this.refresh();
    }

    _saveCurrent() {
        const id = this._currentModelId();
        if (!id) return;
        const trimVal = this.els.trim ? parseFloat(this.els.trim.value) : 0;
        saveOverride(id, { faceTrim: Number.isFinite(trimVal) ? trimVal : 0 });
        this._flashSave();
    }

    _overrideWithCurrent() {
        const id = this._currentModelId();
        const mv = this.modelViewer;
        if (!id || !mv || !mv.model) return;
        // Use the live trim slider value so an unsaved preview is honored
        // when the user clicks Override.
        const liveTrim = this.els.trim ? parseFloat(this.els.trim.value) : NaN;
        const pose = computePose(
            id,
            Number.isFinite(liveTrim) ? { faceTrimOverride: liveTrim } : null,
        );
        const eyeHeight = pose.effectiveAnchorY + pose.faceTrim - mv.model.position.y;
        if (!Number.isFinite(eyeHeight) || eyeHeight <= 0) {
            console.warn("[PlatformHeightController] override produced invalid eye height", eyeHeight);
            return;
        }
        saveOverride(id, {
            eyeHeight,
            detectedEyeHeight: eyeHeight,
            faceTrim: Number.isFinite(liveTrim) ? liveTrim : (pose.faceTrim || 0),
            manualLock: true,
        });
        this._reapply();
        this.refresh();
    }

    _flashSave() {
        const btn = this.els.save;
        if (!btn) return;
        const original = btn.innerHTML;
        btn.innerHTML = '<i class="fas fa-check"></i> Saved!';
        btn.classList.remove("btn-primary");
        btn.classList.add("btn-success");
        setTimeout(() => {
            btn.innerHTML = original;
            btn.classList.remove("btn-success");
            btn.classList.add("btn-primary");
        }, 2000);
    }

    _exportJson() {
        const merged = exportPoses();
        const json = JSON.stringify(merged, null, 2);
        try {
            const blob = new Blob([json], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "character-poses.json";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            console.warn("[PlatformHeightController] download failed, logging instead:", e);
        }
        console.log("[CharacterPoses export]", json);
    }
}

export const ANCHOR_POSE_CONSTANTS = Object.freeze({
    FACE_TRIM_MIN,
    FACE_TRIM_MAX,
    ANCHOR_MIN,
    ANCHOR_MAX,
    SCENE_FRAMING,
});
